import { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from '@/components/ui/sonner';
import { ArrowLeft, LifeBuoy, CheckCircle2, PackageSearch } from 'lucide-react';
import { Turnstile, useHoneypot } from '@/components/Turnstile';

export default function Contacto() {
  const { user } = useAuth();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [captcha, setCaptcha] = useState('');
  const { hp, field: honeypot } = useHoneypot();

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const payload = { ...form, name: form.name || user?.full_name || 'Cliente', email: form.email || user?.email || '', captcha_token: captcha, hp };
      const { data } = await api.post('/support/contact', payload);
      setSent(true);
      toast.success(data.message || 'Mensaje enviado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo enviar el mensaje. Intenta de nuevo.');
    } finally { setLoading(false); }
  };

  return (
    <main className="max-w-2xl mx-auto px-4 py-10" data-testid="contact-page">
      <Link to="/" data-testid="contact-back-home" className="inline-flex min-h-11 items-center gap-2 text-sm text-muted-foreground mb-4"><ArrowLeft className="h-4 w-4" /> Volver a la tienda</Link>
      <div className="flex items-center gap-3 mb-4"><div className="w-12 h-12 rounded-full bg-brand-navy flex items-center justify-center"><LifeBuoy className="h-6 w-6 text-brand-gold" /></div><div><h1 className="font-heading font-bold text-3xl text-foreground" data-testid="contact-title">Contacto y soporte</h1><p className="text-sm text-muted-foreground">Escríbenos y nuestro equipo lo revisará desde el panel interno.</p></div></div>
      <Alert className="mb-6" data-testid="contact-disclosure"><PackageSearch className="h-4 w-4" /><AlertDescription className="text-xs leading-relaxed">Los canales externos (teléfono, redes sociales) todavía no están configurados y no mostramos datos inventados. Para dudas de un pedido, incluye su número; también puedes <Link className="underline" to="/rastrear">rastrear tu pedido</Link> o revisar <Link className="underline" to="/mis-pedidos">Mis pedidos</Link>.</AlertDescription></Alert>
      {sent ? (
        <div className="bg-card border border-border rounded-xl p-6" data-testid="contact-success">
          <Alert><CheckCircle2 className="h-4 w-4" /><AlertDescription>Recibimos tu mensaje. Nuestro equipo lo revisará y te responderá por el correo indicado.</AlertDescription></Alert>
          <Button variant="outline" className="mt-4 min-h-11" data-testid="contact-send-another" onClick={() => { setSent(false); setForm({ name: '', email: '', subject: '', message: '' }); }}>Enviar otro mensaje</Button>
        </div>
      ) : (
        <form onSubmit={submit} className="bg-card border border-border rounded-xl p-6 space-y-4" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label htmlFor="contact-name">Nombre</Label><Input id="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" className="mt-1 h-11" /></div>
            <div><Label htmlFor="contact-email">Correo</Label><Input id="contact-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" className="mt-1 h-11" /></div>
          </div>
          <div><Label htmlFor="contact-subject">Asunto</Label><Input id="contact-subject" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject" className="mt-1 h-11" /></div>
          <div><Label htmlFor="contact-message">Mensaje</Label><Textarea id="contact-message" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" className="mt-1" /></div>
          {honeypot}
          <Turnstile onToken={setCaptcha} />
          <Button type="submit" disabled={loading} data-testid="contact-submit" className="min-h-11 bg-brand-orange text-brand-navy hover:bg-brand-orange-dark">{loading ? 'Enviando…' : 'Enviar mensaje'}</Button>
        </form>
      )}
    </main>
  );
}
